import React, { useMemo, useState } from "react";
import { Download, Printer, Wrench } from "lucide-react";
import { APP_IDS } from "../constants/testIds";
import { TICKET_PRICE, TOTAL_TICKETS, formatUSD } from "../utils/formatters";
import { exportTicketsCsv } from "../utils/exportCsv";
import { SellerActivityReport, SoldTicketsReport, runPrint } from "./PrintReports";
import { api, formatApiError } from "../api/client";

const StatBox = ({ label, value, sublabel, color }) => (
  <div className="border rounded-lg p-4 bg-[color:var(--surface-card)]" style={{ borderColor: "var(--border-default)" }}>
    <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.04em" }}>{label}</p>
    <p className="mt-1 text-2xl font-semibold" style={{ color: color || "var(--ink-primary)" }}>{value}</p>
    {sublabel && <p className="text-xs mt-0.5" style={{ color: "var(--ink-tertiary)" }}>{sublabel}</p>}
  </div>
);

const parseCsv = (text) => {
  // Minimal CSV parser supporting quotes & commas inside quoted fields.
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { header: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQuotes = false;
        else cur += ch;
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ",") { out.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const header = parseLine(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map(parseLine);
  return { header, rows };
};

export const ReportTab = ({
  tickets,
  stats,
  sellers,
  year,
  settings,
  currentUser,
  onCloseYear,
  onStartYear,
  onImportHistory,
  onSyncBrevoContacts,
  showToast,
}) => {
  const isAdmin = currentUser?.role === "admin";
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeErr, setCloseErr] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const [startErr, setStartErr] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importErr, setImportErr] = useState("");
  const [brevoBusy, setBrevoBusy] = useState(false);
  const [brevoMsg, setBrevoMsg] = useState("");
  const [brevoErr, setBrevoErr] = useState("");
  const [importPreview, setImportPreview] = useState(null); // {year, rows}
  const [importYear, setImportYear] = useState(year - 1);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [newYearInput, setNewYearInput] = useState(String(year + 1));
  // Recovery tool state.
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildPreview, setRebuildPreview] = useState(null); // response from dry-run
  const [rebuildErr, setRebuildErr] = useState("");
  const [rebuildMsg, setRebuildMsg] = useState("");
  // Full-table CSV export state.
  const [exportBusy, setExportBusy] = useState(""); // "tickets" | "drawings" | ""
  const [exportErr, setExportErr] = useState("");

  const counts = useMemo(() => {
    let sold = 0, reserved = 0, funds = 0, available = 0;
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const st = tickets[i]?.status;
      if (st === "sold") sold++;
      else if (st === "reserved") reserved++;
      else if (st === "funds_received") funds++;
      else available++;
    }
    return { sold, reserved, funds, available };
  }, [tickets]);

  const pledged = counts.sold + counts.funds;
  const pct = Math.round((pledged / TOTAL_TICKETS) * 100);
  const collectedRevenue = counts.funds * TICKET_PRICE;
  const pendingRevenue = counts.sold * TICKET_PRICE;
  const reservedRevenue = counts.reserved * TICKET_PRICE;

  // Derive leaderboard from CURRENT ticket state, not log entries.
  // Releasing a ticket should remove the seller's credit for that ticket — which the
  // log-based approach couldn't do (log entries are immutable).
  const leaderboard = useMemo(() => {
    const counts2 = {};
    const fundsCounts = {};
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      const name = t.assigned_seller_name;
      if (!name) continue;
      if (t.status === "sold" || t.status === "funds_received") {
        counts2[name] = (counts2[name] || 0) + 1;
      }
      if (t.status === "funds_received") {
        fundsCounts[name] = (fundsCounts[name] || 0) + 1;
      }
    }
    const rows = sellers.map((s) => ({
      name: s.name,
      sold: counts2[s.name] || 0,
      funds: fundsCounts[s.name] || 0,
    }));
    Object.keys(counts2).forEach((name) => {
      if (!rows.find((r) => r.name === name)) rows.push({ name, sold: counts2[name], funds: fundsCounts[name] || 0 });
    });
    return rows.sort((a, b) => (b.sold + b.funds) - (a.sold + a.funds));
  }, [tickets, sellers]);

  const handleCloseYear = () => {
    setCloseErr("");
    setNewYearInput(String(year + 1));
    setCloseConfirmOpen(true);
  };

  const confirmCloseYear = async () => {
    const newYear = Number(newYearInput);
    if (!newYear || newYear === year) { setCloseErr("Enter a new raffle year different from the current one."); return; }
    setCloseBusy(true);
    const res = await onCloseYear(year, newYear);
    setCloseBusy(false);
    if (!res.ok) { setCloseErr(res.error || "Could not close out the year."); return; }
    setCloseConfirmOpen(false);
    showToast?.(`${res.snapshots} tickets archived. Welcome to ${newYear}.`);
  };

  // Rebuild check-ins from the Activity Log. Always dry-runs first so admins
  // see what will be created before any writes happen.
  const runRebuildDryRun = async () => {
    setRebuildBusy(true); setRebuildErr(""); setRebuildMsg("");
    try {
      const { data } = await api.post("/admin/checkins/rebuild-from-log", { dry_run: true });
      setRebuildPreview(data);
    } catch (e) {
      setRebuildErr(formatApiError(e) || "Could not run rebuild preview.");
    } finally { setRebuildBusy(false); }
  };
  const confirmRebuild = async () => {
    setRebuildBusy(true); setRebuildErr("");
    try {
      const { data } = await api.post("/admin/checkins/rebuild-from-log", { dry_run: false });
      setRebuildPreview(null);
      setRebuildMsg(`Rebuilt ${data.written} check-in${data.written === 1 ? "" : "s"} from the activity log.`);
      showToast?.(`${data.written} check-in${data.written === 1 ? "" : "s"} restored`);
    } catch (e) {
      setRebuildErr(formatApiError(e) || "Could not rebuild check-ins.");
    } finally { setRebuildBusy(false); }
  };

  // Download a server-generated CSV export (full ticket table, drawings table, or per-seller settlement).
  const downloadExport = async (kind) => {
    if (exportBusy) return;
    setExportBusy(kind); setExportErr("");
    const config = {
      tickets: { path: "/admin/exports/tickets.csv", filename: `400club-tickets-${year}.csv`, toast: `${year} ticket CSV downloaded` },
      drawings: { path: "/admin/exports/drawings.csv", filename: "400club-drawings.csv", toast: "Drawings CSV downloaded" },
      sellers: { path: "/admin/exports/sellers.csv", filename: `400club-sellers-${year}.csv`, toast: `${year} sellers CSV downloaded` },
    }[kind];
    try {
      const { data } = await api.get(config.path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = config.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.(config.toast);
    } catch (e) {
      setExportErr(formatApiError(e) || "Could not download CSV.");
    } finally { setExportBusy(""); }
  };

  const handleStartYear = () => {
    setStartErr("");
    setStartConfirmOpen(true);
  };

  const confirmStartYear = async () => {
    setStartBusy(true);
    const res = await onStartYear();
    setStartBusy(false);
    if (!res.ok) { setStartErr(res.error || "Could not start the year."); return; }
    setStartConfirmOpen(false);
    showToast?.(`Raffle for ${year} is now open.`);
  };

  const startedAt = settings?.year_started_at ? new Date(settings.year_started_at) : null;
  const startedAtLabel = startedAt && !Number.isNaN(startedAt.getTime())
    ? startedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const daysSinceStart = startedAt && !Number.isNaN(startedAt.getTime())
    ? Math.floor((Date.now() - startedAt.getTime()) / (24 * 60 * 60 * 1000))
    : null;

  const handleFileChange = async (e) => {
    setImportErr(""); setImportMsg(""); setImportPreview(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const { header, rows } = parseCsv(text);
    if (rows.length === 0) { setImportErr("CSV is empty."); return; }
    // Expected columns (case-insensitive): ticket_number (or "ticket"), buyer, phone, status, sold_date (or "date"), seller_name (or "seller")
    const idx = (names) => {
      for (const n of names) {
        const i = header.indexOf(n);
        if (i !== -1) return i;
      }
      return -1;
    };
    const tnIdx = idx(["ticket_number", "ticket", "number"]);
    const buyerIdx = idx(["buyer", "buyer_name", "name"]);
    const phoneIdx = idx(["phone"]);
    const statusIdx = idx(["status"]);
    const dateIdx = idx(["sold_date", "date"]);
    const sellerIdx = idx(["seller_name", "seller"]);
    if (tnIdx === -1 || buyerIdx === -1) {
      setImportErr("CSV must include at least 'ticket_number' and 'buyer' columns.");
      return;
    }
    const parsed = [];
    rows.forEach((r) => {
      const n = parseInt(r[tnIdx], 10);
      if (!n || n < 1 || n > TOTAL_TICKETS) return;
      let status = statusIdx >= 0 ? (r[statusIdx] || "").toLowerCase().trim() : "funds_received";
      if (!["sold", "reserved", "funds_received"].includes(status)) status = "funds_received";
      parsed.push({
        ticket_number: n,
        buyer: buyerIdx >= 0 ? r[buyerIdx] || "" : "",
        phone: phoneIdx >= 0 ? r[phoneIdx] || "" : "",
        status,
        sold_date: dateIdx >= 0 ? r[dateIdx] || "" : "",
        seller_name: sellerIdx >= 0 ? r[sellerIdx] || "" : "",
      });
    });
    if (parsed.length === 0) { setImportErr("No valid rows found in CSV."); return; }
    setImportPreview({ rows: parsed });
  };

  const handleImportConfirm = async () => {
    if (!importPreview) return;
    setImportBusy(true);
    setImportErr(""); setImportMsg("");
    const res = await onImportHistory({ year: importYear, rows: importPreview.rows, replace: replaceExisting });
    setImportBusy(false);
    if (!res.ok) { setImportErr(res.error || "Import failed."); return; }
    setImportMsg(`Imported ${res.imported} rows for ${res.year}.`);
    setImportPreview(null);
    setReplaceExisting(false);
    showToast?.(`Imported ${res.imported} historical rows`);
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>Summary report — {year}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="print-seller-activity"
              onClick={() => runPrint("seller-activity")}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
            >
              <Printer className="h-4 w-4" />
              Print: Seller activity
            </button>
            <button
              data-testid="print-sold-tickets"
              onClick={() => runPrint("sold-tickets")}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
            >
              <Printer className="h-4 w-4" />
              Print: Tickets turned in
            </button>
            <button
              data-testid={APP_IDS.exportCsv}
              onClick={() => exportTicketsCsv(tickets, year)}
              className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90"
              style={{ background: "var(--brand-primary)" }}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="Reserved" value={counts.reserved} color="var(--brand-primary)" />
          <StatBox label="Sold (pending)" value={counts.sold} color="var(--status-sold-bg)" />
          <StatBox label="Funds received" value={counts.funds} color="var(--semantic-danger)" />
          <StatBox label="Available" value={counts.available} />
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatBox
            label="Collected"
            value={formatUSD(collectedRevenue)}
            sublabel={`${counts.funds} ticket${counts.funds === 1 ? "" : "s"} × $${TICKET_PRICE}`}
            color="var(--brand-primary)"
          />
          <StatBox
            label="Outstanding"
            value={formatUSD(pendingRevenue)}
            sublabel={`${counts.sold} sold ticket${counts.sold === 1 ? "" : "s"} awaiting payment`}
            color="var(--status-sold-bg)"
          />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium" style={{ color: "var(--ink-primary)" }}>Pledged progress ({pledged}/{TOTAL_TICKETS})</span>
            <span style={{ color: "var(--ink-tertiary)" }}>{pct}%</span>
          </div>
          <div className="w-full rounded" style={{ background: "var(--border-default)", height: "10px", borderRadius: "4px", overflow: "hidden" }}>
            <div
              className="h-full transition-all"
              style={{ background: "var(--brand-primary)", width: `${pct}%`, borderRadius: "4px" }}
            />
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--ink-primary)" }}>Revenue summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--ink-tertiary)" }}>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 pr-3 font-medium">Tickets</th>
                  <th className="py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ color: "var(--ink-primary)" }}>
                <tr style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-2 pr-3">Funds received (collected)</td>
                  <td className="py-2 pr-3">{counts.funds}</td>
                  <td className="py-2 font-medium" style={{ color: "var(--semantic-danger)" }}>{formatUSD(collectedRevenue)}</td>
                </tr>
                <tr style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-2 pr-3">Sold (awaiting payment)</td>
                  <td className="py-2 pr-3">{counts.sold}</td>
                  <td className="py-2 font-medium" style={{ color: "var(--status-sold-bg)" }}>{formatUSD(pendingRevenue)}</td>
                </tr>
                <tr style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-2 pr-3">Reserved (pending sale)</td>
                  <td className="py-2 pr-3">{counts.reserved}</td>
                  <td className="py-2 font-medium" style={{ color: "var(--brand-primary)" }}>{formatUSD(reservedRevenue)}</td>
                </tr>
                <tr style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-2 pr-3">Available</td>
                  <td className="py-2 pr-3">{counts.available}</td>
                  <td className="py-2" style={{ color: "var(--ink-tertiary)" }}>—</td>
                </tr>
                <tr style={{ borderColor: "var(--border-default)" }} className="font-semibold">
                  <td className="py-2 pr-3">Total potential</td>
                  <td className="py-2 pr-3">{TOTAL_TICKETS}</td>
                  <td className="py-2">{formatUSD(TOTAL_TICKETS * TICKET_PRICE)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {leaderboard.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--ink-primary)" }}>Seller leaderboard</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ color: "var(--ink-tertiary)" }}>
                    <th className="py-2 pr-3 font-medium">Seller</th>
                    <th className="py-2 pr-3 font-medium">Sold</th>
                    <th className="py-2 pr-3 font-medium">Funds received</th>
                    <th className="py-2 font-medium">Collected</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ color: "var(--ink-primary)" }}>
                  {leaderboard.map((row) => (
                    <tr key={row.name} style={{ borderColor: "var(--border-default)" }}>
                      <td className="py-2 pr-3">{row.name}</td>
                      <td className="py-2 pr-3">{row.sold}</td>
                      <td className="py-2 pr-3">{row.funds}</td>
                      <td className="py-2 font-medium" style={{ color: "var(--brand-primary)" }}>{formatUSD(row.funds * TICKET_PRICE)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Admin: start year */}
      {isAdmin && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="start-year-card">
          <h2 className="text-base font-semibold mb-1" style={{ color: "var(--ink-primary)" }}>Open the {year} raffle</h2>
          {startedAtLabel ? (
            <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }} data-testid="start-year-status">
              The {year} raffle was opened on <strong style={{ color: "var(--ink-primary)" }}>{startedAtLabel}</strong>
              {daysSinceStart != null && (
                <> · <span>{daysSinceStart} day{daysSinceStart === 1 ? "" : "s"} ago</span></>
              )}
              .{" "}
              {daysSinceStart != null && daysSinceStart > 30
                ? "The returning-buyers banner has aged out (auto-hides after 30 days)."
                : "Returning-buyers banner is still active for the opening month."}
            </p>
          ) : (
            <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }} data-testid="start-year-status">
              The {year} raffle has not been opened yet. Click below when you start taking orders — the &ldquo;returning buyers&rdquo; banner will then auto-hide 30 days from today.
            </p>
          )}
          {startErr && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="start-year-error">{startErr}</p>}
          <button
            type="button"
            onClick={handleStartYear}
            disabled={startBusy}
            data-testid="start-year-btn"
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--semantic-success)" }}
          >
            {startBusy ? "Saving…" : startedAtLabel ? `Re-stamp opening date to today` : `Open the ${year} raffle`}
          </button>
        </div>
      )}

      {/* Admin: history import */}
      {isAdmin && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="history-import-card">
          <h2 className="text-base font-semibold mb-1" style={{ color: "var(--ink-primary)" }}>Import past year sales</h2>
          <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }}>
            Upload a CSV from a previous raffle year. Required headers: <code>ticket_number, buyer</code>. Optional: <code>phone, status, sold_date, seller_name</code>.
            Imported rows are appended to the history record and surface as &ldquo;returning buyers&rdquo; on the grid and ticket modal.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: "var(--ink-tertiary)" }}>Year</label>
              <input
                data-testid="import-year"
                type="number"
                min="2000"
                max={year - 1}
                value={importYear}
                onChange={(e) => setImportYear(Number(e.target.value))}
                className="w-28 rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                style={{ borderColor: "var(--border-default)" }}
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: "var(--ink-tertiary)" }}>CSV file</label>
              <input
                data-testid="import-file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="text-sm"
              />
            </div>
            <label className="text-xs flex items-center gap-2" style={{ color: "var(--ink-tertiary)" }}>
              <input
                data-testid="import-replace"
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
              />
              Replace existing history for that year
            </label>
          </div>

          {importPreview && (
            <div className="mt-4" data-testid="import-preview">
              <p className="text-sm mb-2" style={{ color: "var(--ink-primary)" }}>
                <strong>{importPreview.rows.length}</strong> rows ready to import for <strong>{importYear}</strong>.
              </p>
              <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border-default)" }}>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr style={{ color: "var(--ink-tertiary)" }}>
                        <th className="text-left px-2 py-1.5">Ticket</th>
                        <th className="text-left px-2 py-1.5">Buyer</th>
                        <th className="text-left px-2 py-1.5">Phone</th>
                        <th className="text-left px-2 py-1.5">Status</th>
                        <th className="text-left px-2 py-1.5">Seller</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.slice(0, 50).map((r) => (
                        <tr key={`${r.ticket_number}-${r.buyer}`} className="border-t" style={{ borderColor: "var(--border-default)" }}>
                          <td className="px-2 py-1">{r.ticket_number}</td>
                          <td className="px-2 py-1">{r.buyer}</td>
                          <td className="px-2 py-1">{r.phone}</td>
                          <td className="px-2 py-1">{r.status}</td>
                          <td className="px-2 py-1">{r.seller_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.rows.length > 50 && (
                  <p className="text-xs px-2 py-1.5 bg-gray-50" style={{ color: "var(--ink-tertiary)" }}>
                    Showing first 50 of {importPreview.rows.length}.
                  </p>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleImportConfirm}
                  disabled={importBusy}
                  data-testid="import-confirm"
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ background: "var(--semantic-success)" }}
                >
                  {importBusy ? "Importing…" : `Import ${importPreview.rows.length} rows`}
                </button>
                <button
                  type="button"
                  onClick={() => setImportPreview(null)}
                  data-testid="import-cancel"
                  className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)]"
                  style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {importErr && <p className="mt-3 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="import-error">{importErr}</p>}
          {importMsg && <p className="mt-3 text-sm font-medium" style={{ color: "var(--semantic-success)" }} data-testid="import-success">{importMsg}</p>}

          {settings?.history_years?.length > 0 && (
            <p className="mt-4 text-xs" style={{ color: "var(--ink-tertiary)" }}>
              History on file: {settings.history_years.join(", ")}
            </p>
          )}
        </div>
      )}

      {/* Admin: Brevo contact sync */}
      {isAdmin && onSyncBrevoContacts && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="brevo-sync-card">
          <h2 className="text-base font-semibold mb-1" style={{ color: "var(--ink-primary)" }}>Sync buyers to Brevo</h2>
          <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }}>
            Push every buyer with an email address (current year + all archived years) into the &ldquo;Red Hook Elks 400 Club Buyers&rdquo; Brevo contact list. New/updated buyers sync automatically after each sale — use this button to backfill everyone else or re-sync after a bulk edit.
          </p>
          {brevoMsg && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-success)" }} data-testid="brevo-sync-msg">{brevoMsg}</p>}
          {brevoErr && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="brevo-sync-error">{brevoErr}</p>}
          <button
            type="button"
            onClick={async () => {
              setBrevoBusy(true); setBrevoMsg(""); setBrevoErr("");
              const res = await onSyncBrevoContacts();
              setBrevoBusy(false);
              if (!res.ok) { setBrevoErr(res.error || "Sync failed."); return; }
              setBrevoMsg(`Synced ${res.synced} of ${res.total_emails} buyers to Brevo${res.failed ? ` (${res.failed} failed — see server logs)` : ""}.`);
              showToast?.(`${res.synced} buyers synced to Brevo`);
            }}
            disabled={brevoBusy}
            data-testid="brevo-sync-btn"
            className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--brand-primary)" }}
          >
            {brevoBusy ? "Syncing…" : "Sync all buyers to Brevo"}
          </button>
        </div>
      )}

      {/* Admin: full-table CSV exports (tickets for current year + drawings across all years) */}
      {isAdmin && (
        <div
          className="bg-[color:var(--surface-card)] border rounded-lg p-5"
          style={{ borderColor: "var(--border-default)" }}
          data-testid="exports-card"
        >
          <h2 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ color: "var(--ink-primary)" }}>
            <Download className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />
            Data exports
          </h2>
          <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }}>
            Download the complete tickets table (current raffle year), the drawings table (all years), and a per-seller settlement summary as CSV files. Open directly in Excel or Google Sheets.
          </p>
          {exportErr && (
            <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="exports-error">
              {exportErr}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => downloadExport("tickets")}
              disabled={!!exportBusy}
              data-testid="export-tickets-csv"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
            >
              <Download className="h-4 w-4" />
              {exportBusy === "tickets" ? "Preparing…" : `Download Tickets CSV (${year})`}
            </button>
            <button
              type="button"
              onClick={() => downloadExport("drawings")}
              disabled={!!exportBusy}
              data-testid="export-drawings-csv"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
            >
              <Download className="h-4 w-4" />
              {exportBusy === "drawings" ? "Preparing…" : "Download Drawings CSV (all years)"}
            </button>
            <button
              type="button"
              onClick={() => downloadExport("sellers")}
              disabled={!!exportBusy}
              data-testid="export-sellers-csv"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
            >
              <Download className="h-4 w-4" />
              {exportBusy === "sellers" ? "Preparing…" : `Download Sellers CSV (${year})`}
            </button>
          </div>
        </div>
      )}

      {/* Admin: rebuild check-ins from activity log (recovery tool) */}
      {isAdmin && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="rebuild-checkins-card">
          <h2 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ color: "var(--ink-primary)" }}>
            <Wrench className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />
            Rebuild check-ins from Activity Log
          </h2>
          <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }}>
            If a drawing-night check-in row goes missing, this tool replays every <code>checkin</code> entry in the activity log (in order) and restores the missing rows. It never overwrites an existing check-in and never touches the log itself. Always previews before writing.
          </p>
          {rebuildErr && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="rebuild-error">{rebuildErr}</p>}
          {rebuildMsg && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-success)" }} data-testid="rebuild-success">{rebuildMsg}</p>}
          {!rebuildPreview ? (
            <button
              type="button"
              onClick={runRebuildDryRun}
              disabled={rebuildBusy}
              data-testid="rebuild-preview-btn"
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors hover:opacity-90 disabled:opacity-50 border"
              style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
            >
              {rebuildBusy ? "Scanning log…" : "Preview rebuild"}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border p-3 text-sm space-y-1" style={{ background: "var(--surface-subtle)", borderColor: "var(--border-default)", color: "var(--ink-primary)" }} data-testid="rebuild-preview">
                <div><strong>{rebuildPreview.log_rows_scanned}</strong> log rows scanned</div>
                <div><strong>{rebuildPreview.final_checkins_from_log}</strong> net check-ins after replay ({rebuildPreview.already_present_in_db} already in DB · <strong style={{ color: "var(--brand-primary)" }}>{rebuildPreview.would_create} to create</strong>)</div>
                {rebuildPreview.unparseable_rows > 0 && (
                  <div style={{ color: "var(--semantic-warning)" }}>{rebuildPreview.unparseable_rows} unparseable rows (skipped)</div>
                )}
                {rebuildPreview.unmapped_drawings?.length > 0 && (
                  <div style={{ color: "var(--semantic-warning)" }}>
                    Unmatched drawing names (renamed?): {rebuildPreview.unmapped_drawings.map((u) => `${u.drawing_name} (${u.event_count})`).join(", ")}
                  </div>
                )}
              </div>
              {rebuildPreview.sample?.length > 0 && (
                <div className="border rounded-md max-h-64 overflow-y-auto" style={{ borderColor: "var(--border-default)" }}>
                  <table className="w-full text-xs">
                    <thead className="sticky top-0" style={{ background: "var(--surface-subtle)", color: "var(--ink-tertiary)" }}>
                      <tr>
                        <th className="text-left px-2 py-1.5">Ticket</th>
                        <th className="text-left px-2 py-1.5">Drawing</th>
                        <th className="text-left px-2 py-1.5">Buyer</th>
                        <th className="text-left px-2 py-1.5">Guest</th>
                        <th className="text-left px-2 py-1.5">When</th>
                        <th className="text-left px-2 py-1.5">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rebuildPreview.sample.map((r) => (
                        <tr key={`${r.drawing_id}-${r.ticket_number}`} className="border-t" style={{ borderColor: "var(--border-default)" }}>
                          <td className="px-2 py-1.5 font-semibold" style={{ color: "var(--brand-primary)" }}>#{r.ticket_number}</td>
                          <td className="px-2 py-1.5">{r.drawing_name}</td>
                          <td className="px-2 py-1.5">{r.buyer_name || "\u2014"}</td>
                          <td className="px-2 py-1.5">{r.guest_attending ? "+1" : "\u2014"}</td>
                          <td className="px-2 py-1.5" style={{ color: "var(--ink-tertiary)" }}>{r.checked_in_at?.slice(0, 16).replace("T", " ") || ""}</td>
                          <td className="px-2 py-1.5" style={{ color: "var(--ink-tertiary)" }}>{r.checked_in_by_name || "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rebuildPreview.would_create > rebuildPreview.sample.length && (
                    <div className="px-2 py-1.5 text-xs text-center" style={{ background: "var(--surface-subtle)", color: "var(--ink-tertiary)" }}>
                      Showing first {rebuildPreview.sample.length} of {rebuildPreview.would_create} rows.
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={confirmRebuild}
                  disabled={rebuildBusy || rebuildPreview.would_create === 0}
                  data-testid="rebuild-confirm-btn"
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ background: "var(--brand-primary)" }}
                >
                  {rebuildBusy ? "Rebuilding…" : `Rebuild ${rebuildPreview.would_create} check-in${rebuildPreview.would_create === 1 ? "" : "s"} now`}
                </button>
                <button
                  type="button"
                  onClick={() => { setRebuildPreview(null); setRebuildMsg(""); setRebuildErr(""); }}
                  className="px-4 py-2 text-sm font-medium rounded-md border transition-colors"
                  style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin: year close-out */}
      {isAdmin && (
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="close-year-card">
          <h2 className="text-base font-semibold mb-1" style={{ color: "var(--ink-primary)" }}>Close out {year}</h2>
          <p className="text-sm mb-3" style={{ color: "var(--ink-tertiary)" }}>
            Archives the current year (sold/reserved/funds-received tickets) to history, then resets the live grid to all-available so the new year can begin. The activity log is cleared. Ticket assignments are preserved.
          </p>
          {closeErr && <p className="mb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="close-year-error">{closeErr}</p>}
          <button
            type="button"
            onClick={handleCloseYear}
            disabled={closeBusy}
            data-testid="close-year-btn"
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--semantic-danger)" }}
          >
            {closeBusy ? "Closing…" : `Close out ${year}`}
          </button>
        </div>
      )}
      {/* Print-only reports (hidden on screen via CSS) */}
      <SellerActivityReport tickets={tickets} sellers={sellers} year={year} />
      <SoldTicketsReport tickets={tickets} year={year} />

      {/* Start-year confirm modal */}
      {startConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-label="Confirm open raffle"
          data-testid="start-year-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !startBusy) setStartConfirmOpen(false); }}
          style={{ background: "rgba(32, 31, 30, 0.55)" }}
        >
          <div className="bg-[color:var(--surface-card)] rounded-lg w-full max-w-md border shadow-lg" style={{ borderColor: "var(--border-default)" }}>
            <div className="px-6 pt-5 pb-3">
              <h3 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>Open the {year} raffle?</h3>
            </div>
            <div className="px-6 pb-4 text-sm" style={{ color: "var(--ink-tertiary)" }}>
              <p>This will stamp today as the opening date for {year}. The &ldquo;returning buyers&rdquo; banner will auto-hide 30 days from now.</p>
            </div>
            {startErr && <p className="px-6 pb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="start-year-modal-error">{startErr}</p>}
            <div className="px-6 py-4 border-t flex flex-wrap items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
              <button
                type="button"
                onClick={() => setStartConfirmOpen(false)}
                disabled={startBusy}
                data-testid="start-year-cancel"
                className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] transition-colors hover:border-[color:var(--ink-tertiary)] disabled:opacity-50"
                style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmStartYear}
                disabled={startBusy}
                data-testid="start-year-confirm"
                className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--semantic-success)" }}
              >
                {startBusy ? "Saving…" : "Open raffle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close-year confirm modal */}
      {closeConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-label="Confirm close-out year"
          data-testid="close-year-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !closeBusy) setCloseConfirmOpen(false); }}
          style={{ background: "rgba(32, 31, 30, 0.55)" }}
        >
          <div className="bg-[color:var(--surface-card)] rounded-lg w-full max-w-md border shadow-lg" style={{ borderColor: "var(--border-default)" }}>
            <div className="px-6 pt-5 pb-3">
              <h3 className="text-base font-semibold" style={{ color: "var(--semantic-danger)" }}>Close out {year}?</h3>
            </div>
            <div className="px-6 pb-4 text-sm space-y-3" style={{ color: "var(--ink-tertiary)" }}>
              <p>
                This will archive every sold / reserved / funds-received ticket to the history record, reset the live grid to all-available, and clear the activity log.
                Ticket assignments are preserved. <strong style={{ color: "var(--semantic-danger)" }}>This cannot be undone.</strong>
              </p>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--ink-tertiary)" }}>
                  New raffle year
                </label>
                <input
                  data-testid="close-year-new-year"
                  type="number"
                  min={year + 1}
                  max={year + 50}
                  value={newYearInput}
                  onChange={(e) => setNewYearInput(e.target.value)}
                  disabled={closeBusy}
                  className="w-32 rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ borderColor: "var(--border-default)" }}
                />
              </div>
            </div>
            {closeErr && <p className="px-6 pb-2 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="close-year-modal-error">{closeErr}</p>}
            <div className="px-6 py-4 border-t flex flex-wrap items-center justify-end gap-2" style={{ borderColor: "var(--border-default)" }}>
              <button
                type="button"
                onClick={() => setCloseConfirmOpen(false)}
                disabled={closeBusy}
                data-testid="close-year-cancel"
                className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] transition-colors hover:border-[color:var(--ink-tertiary)] disabled:opacity-50"
                style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmCloseYear}
                disabled={closeBusy}
                data-testid="close-year-confirm"
                className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ background: "var(--semantic-danger)" }}
              >
                {closeBusy ? "Closing…" : `Close out ${year}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
